import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Plus, Search, Edit, Trash2, Eye, ChevronLeft, 
  Lock, Settings, PawPrint, Car, Building2, Save, X,
  CheckCircle, XCircle, AlertTriangle, Calendar, Clock, DollarSign,
  BookUser, LayoutDashboard, Database, Shield, Mail, KeyRound, Truck, Megaphone,
  Upload, FileText, Download, File, User, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import AccountingPanel from './AccountingPanel';
import CustomerRegistry from './CustomerRegistry';
import DashboardPanel from './DashboardPanel';
import BackupPanel from './BackupPanel';
import ComboTransportAdmin from './ComboTransportAdmin';
import InfoBannerAdmin from './InfoBannerAdmin';
import AdminCalendar from './admin/AdminCalendar';
import AnalyticsPanel from './AnalyticsPanel';
import AdminManualInvoices from './admin/AdminManualInvoices';
import AdminManualQuotes from './admin/AdminManualQuotes';
import AdminNewsletter from './admin/AdminNewsletter';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('dashboard'); // 'dashboard', 'list', 'detail', 'edit', 'new', 'settings', 'calendar', 'accounting', 'customers', 'backup', 'combo-transport', 'banner', 'analytics'
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // E-Mail Status
  const [emailStatus, setEmailStatus] = useState({ failedCount: 0, failedEmails: [], hasErrors: false });
  const [emailErrorDialogOpen, setEmailErrorDialogOpen] = useState(false);
  const [emailPreviewIndex, setEmailPreviewIndex] = useState(null);
  const [emailRetryingIndex, setEmailRetryingIndex] = useState(null);
  
  // Ausstehende Registrierungen
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  
  // 2FA State
  const [loginStep, setLoginStep] = useState('password'); // 'password', '2fa'
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAValidityMinutes, setTwoFAValidityMinutes] = useState(5);
  const [maskedEmail, setMaskedEmail] = useState('');
  
  // Kalender-Blockierung State
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState({
    date: '',
    slots: [],
    reason: '',
    allDay: true
  });
  
  // Admin-Fahrten State
  const [adminRides, setAdminRides] = useState([]);
  const [selectedRideIds, setSelectedRideIds] = useState([]); // Mehrfachauswahl zum Löschen
  const [showRideForm, setShowRideForm] = useState(false);
  const [newRide, setNewRide] = useState({
    date: '',
    timeSlot: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    memberNumber: '',
    petName: '',
    petType: 'Hund',
    startAddress: '',
    endAddress: '',
    rideType: 'tierarzt',
    price: 0,
    notes: '',
    paymentStatus: 'offen',
    deductFromFlatrate: false,
    isRecurring: false,
    recurringWeekdays: [],  // Ausgewählte Wochentage (0=Mo, 1=Di, ..., 6=So)
    recurringWeeks: 4,      // Anzahl Wochen
    timeSlots: []           // Mehrere Zeitslots möglich
  });
  
  const WEEKDAYS = [
    { value: 0, label: 'Mo', fullLabel: 'Montag' },
    { value: 1, label: 'Di', fullLabel: 'Dienstag' },
    { value: 2, label: 'Mi', fullLabel: 'Mittwoch' },
    { value: 3, label: 'Do', fullLabel: 'Donnerstag' },
    { value: 4, label: 'Fr', fullLabel: 'Freitag' },
    { value: 5, label: 'Sa', fullLabel: 'Samstag' },
    { value: 6, label: 'So', fullLabel: 'Sonntag' }
  ];
  
  // Zeitslots für Kundenansicht (normale Geschäftszeiten)
  const TIME_SLOTS_CUSTOMER = [
    '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];
  
  // Zeitslots für Admin (alle 15 Minuten, 0:00 - 23:45)
  const TIME_SLOTS = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  
  const RIDE_TYPES = [
    { value: 'tierarzt', label: 'Tierarztfahrt' },
    { value: 'tierklinik', label: 'Tierklinikfahrt' },
    { value: 'sonstiges', label: 'Sonstige Fahrt' }
  ];
  
  const PET_TYPES = ['Hund', 'Katze', 'Kleintier', 'Vogel', 'Sonstiges'];
  
  // Helper to get stored password
  const storedPassword = () => sessionStorage.getItem('adminPassword') || password;
  
  // Formular-State für neues/bearbeitetes Mitglied
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    pets: [],
    notes: ''
  });

  // Dokumenten-Upload State
  const [memberDocuments, setMemberDocuments] = useState([]);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('vertrag');
  const fileInputRef = useRef(null);

  // Passwort ändern State
  const [passwordChange, setPasswordChange] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  // Step 1: Login with password - request 2FA code
  const handleRequestTwoFA = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login/request-2fa`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      
      if (response.data.requires2FA === false) {
        // 2FA is disabled, login directly
        sessionStorage.setItem('adminPassword', password);
        setIsAuthenticated(true);
        loadMembers();
        loadStats();
        toast.success('Erfolgreich angemeldet');
      } else {
        // 2FA required, show code input
        setLoginStep('2fa');
        setTwoFAValidityMinutes(response.data.validityMinutes || 5);
        // Extract masked email from message
        const match = response.data.message?.match(/an (.+?) gesendet/);
        if (match) setMaskedEmail(match[1]);
        toast.success('Bestätigungscode wurde per E-Mail gesendet');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiges Passwort');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify 2FA code
  const handleVerifyTwoFA = async (e) => {
    e.preventDefault();
    if (twoFACode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login/verify-2fa`, {}, {
        headers: { 
          'X-Admin-Password': password,
          'X-2FA-Code': twoFACode
        }
      });
      
      sessionStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
      setLoginStep('password');
      setTwoFACode('');
      loadMembers();
      loadStats();
      toast.success('Erfolgreich angemeldet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend 2FA code
  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login/request-2fa`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      setTwoFACode('');
      toast.success('Neuer Code wurde gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden des Codes');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel 2FA and go back to password
  const handleCancelTwoFA = () => {
    setLoginStep('password');
    setTwoFACode('');
    setPassword('');
  };

  // Legacy login (kept for backwards compatibility)
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/verify-password`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      sessionStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
      loadMembers();
      loadStats();
      toast.success('Erfolgreich angemeldet');
    } catch (error) {
      toast.error('Ungültiges Passwort');
    } finally {
      setIsLoading(false);
    }
  };

  // Mitglieder laden
  const loadMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/members`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Members loading error:', error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        toast.error('Sitzung abgelaufen - bitte neu anmelden');
      } else {
        toast.error(`Fehler beim Laden der Mitglieder: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // E-Mail Status laden
  const loadEmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-status`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Email status error:', error);
    }
  };

  // E-Mail Fehler löschen
  const clearEmailErrors = async () => {
    try {
      await axios.delete(`${API}/admin/email-status/clear`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setEmailStatus({ failedCount: 0, failedEmails: [], hasErrors: false });
      toast.success('E-Mail-Fehler gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const retryFailedEmail = async (index) => {
    setEmailRetryingIndex(index);
    try {
      const response = await axios.post(
        `${API}/admin/email-status/retry/${index}`,
        {},
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      if (response.data?.success) {
        toast.success('E-Mail erfolgreich erneut versendet');
        // Fehler-Liste neu laden
        await loadEmailStatus();
      } else {
        toast.error(response.data?.error || 'Erneuter Versand fehlgeschlagen');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erneuter Versand fehlgeschlagen';
      toast.error(msg);
    } finally {
      setEmailRetryingIndex(null);
    }
  };

  // Ausstehende Registrierungen laden
  const loadPendingRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-registrations`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setPendingRegistrations(response.data);
    } catch (error) {
      console.error('Pending registrations error:', error);
    }
  };

  // Zahlung bestätigen und Mitglied aktivieren
  const confirmPayment = async (registrationId) => {
    try {
      const response = await axios.post(
        `${API}/admin/pending-registrations/${registrationId}/confirm-payment`,
        {},
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      toast.success(response.data.message || 'Zahlung bestätigt!');
      loadPendingRegistrations();
      loadMembers();
      loadStats();
    } catch (error) {
      console.error('Confirm payment error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Bestätigen der Zahlung');
    }
  };

  // Registrierung löschen
  const deletePendingRegistration = async (registrationId) => {
    if (!window.confirm('Möchten Sie diese Registrierung wirklich löschen?')) return;
    try {
      await axios.delete(
        `${API}/admin/pending-registrations/${registrationId}`,
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      toast.success('Registrierung gelöscht');
      loadPendingRegistrations();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Rundmail senden
  // Statistiken laden
  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  // Mitglied erstellen
  const handleCreateMember = async () => {
    if (!formData.name) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/members`, formData, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      toast.success(`Mitglied #${String(response.data.memberNumber).padStart(6, '0')} erstellt`);
      loadMembers();
      loadStats();
      setView('list');
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setIsLoading(false);
    }
  };

  // Mitglied aktualisieren
  const handleUpdateMember = async () => {
    setIsLoading(true);
    try {
      await axios.put(`${API}/admin/members/${selectedMember.id}`, formData, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      toast.success('Mitglied aktualisiert');
      loadMembers();
      setView('detail');
      // Aktualisiere selectedMember
      const updated = await axios.get(`${API}/admin/members/${selectedMember.id}`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setSelectedMember(updated.data);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setIsLoading(false);
    }
  };

  // Mitglied löschen
  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Mitglied wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/admin/members/${memberId}`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      toast.success('Mitglied gelöscht');
      loadMembers();
      loadStats();
      setView('list');
      setSelectedMember(null);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Mitgliedschaft hinzufügen
  const handleAddMembership = async (petId) => {
    try {
      await axios.post(
        `${API}/admin/members/${selectedMember.id}/memberships?pet_id=${petId}`,
        {},
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      toast.success('Mitgliedschaft hinzugefügt');
      // Mitglied neu laden
      const response = await axios.get(`${API}/admin/members/${selectedMember.id}`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setSelectedMember(response.data);
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Mitgliedschaft');
    }
  };

  // Fahrt nutzen/wiederherstellen
  const handleTripAction = async (membershipId, tripType, action) => {
    try {
      await axios.put(
        `${API}/admin/members/${selectedMember.id}/memberships/${membershipId}/trips?trip_type=${tripType}&action=${action}`,
        {},
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      toast.success(action === 'use' ? 'Fahrt als genutzt markiert' : 'Fahrt wiederhergestellt');
      // Mitglied neu laden
      const response = await axios.get(`${API}/admin/members/${selectedMember.id}`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setSelectedMember(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  // Passwort ändern
  const handleChangePassword = async () => {
    if (passwordChange.new !== passwordChange.confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (passwordChange.new.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/change-password`, {}, {
        headers: {
          'X-Current-Password': passwordChange.current,
          'X-New-Password': passwordChange.new
        }
      });
      sessionStorage.setItem('adminPassword', passwordChange.new);
      toast.success('Passwort geändert');
      setPasswordChange({ current: '', new: '', confirm: '' });
      setView('list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern');
    }
  };

  // ==================== DOKUMENTEN-VERWALTUNG ====================

  // Dokumente eines Mitglieds laden
  const loadMemberDocuments = async (memberId) => {
    try {
      const response = await axios.get(`${API}/admin/members/${memberId}/documents`, {
        headers: { 'X-Admin-Password': storedPassword() }
      });
      setMemberDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setMemberDocuments([]);
    }
  };

  // Dokument hochladen
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', uploadDocType);

    try {
      await axios.post(
        `${API}/admin/members/${selectedMember.id}/documents`,
        formData,
        {
          headers: {
            'X-Admin-Password': storedPassword(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Dokument hochgeladen');
      loadMemberDocuments(selectedMember.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setIsUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Dokument herunterladen
  const handleDownloadDocument = async (document) => {
    try {
      const response = await axios.get(
        `${API}/admin/members/${selectedMember.id}/documents/${document.id}`,
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      
      // Create blob from base64
      const byteCharacters = atob(response.data.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.mimeType });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    }
  };

  // Dokument löschen
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;

    try {
      await axios.delete(
        `${API}/admin/members/${selectedMember.id}/documents/${documentId}`,
        { headers: { 'X-Admin-Password': storedPassword() } }
      );
      toast.success('Dokument gelöscht');
      loadMemberDocuments(selectedMember.id);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Dokumente laden wenn Mitglied ausgewählt wird
  useEffect(() => {
    if (selectedMember?.id && view === 'detail') {
      loadMemberDocuments(selectedMember.id);
    }
  }, [selectedMember?.id, view]);

  // E-Mail Status regelmäßig prüfen
  useEffect(() => {
    if (isAuthenticated) {
      loadEmailStatus();
      loadPendingRegistrations();
      const interval = setInterval(() => {
        loadEmailStatus();
        loadPendingRegistrations();
      }, 60000); // Alle 60 Sekunden
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // ==================== KALENDER-BLOCKIERUNG ====================
  
  // Blockierte Termine laden
  const loadBlockedDates = async () => {
    try {
      const response = await axios.get(`${API}/bookings/admin/blocked-dates?password=${encodeURIComponent(storedPassword())}`);
      setBlockedDates(response.data);
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  // Termin blockieren
  const handleBlockDate = async () => {
    if (!newBlockedDate.date) {
      toast.error('Bitte wählen Sie ein Datum');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API}/bookings/admin/blocked-dates?password=${encodeURIComponent(storedPassword())}`,
        {
          date: newBlockedDate.date,
          slots: newBlockedDate.allDay ? [] : newBlockedDate.slots,
          reason: newBlockedDate.reason
        }
      );
      toast.success('Termin blockiert');
      loadBlockedDates();
      setNewBlockedDate({ date: '', slots: [], reason: '', allDay: true });
    } catch (error) {
      toast.error('Fehler beim Blockieren');
    } finally {
      setIsLoading(false);
    }
  };

  // Blockierung aufheben
  const handleUnblockDate = async (date) => {
    if (!window.confirm(`Blockierung für ${date} aufheben?`)) return;
    
    try {
      await axios.delete(`${API}/bookings/admin/blocked-dates/${date}?password=${encodeURIComponent(storedPassword())}`);
      toast.success('Blockierung aufgehoben');
      loadBlockedDates();
    } catch (error) {
      toast.error('Fehler beim Aufheben der Blockierung');
    }
  };

  // Zeitslot toggle
  const toggleSlot = (slot) => {
    setNewBlockedDate(prev => ({
      ...prev,
      slots: prev.slots.includes(slot)
        ? prev.slots.filter(s => s !== slot)
        : [...prev.slots, slot]
    }));
  };

  // ==================== ADMIN FAHRTEN ====================
  
  // Admin-Fahrten laden
  const loadAdminRides = async () => {
    try {
      const response = await axios.get(`${API}/bookings/admin/rides?password=${encodeURIComponent(storedPassword())}`);
      setAdminRides(response.data);
    } catch (error) {
      console.error('Error loading admin rides:', error);
    }
  };

  // Berechne alle Termine basierend auf Wochentagen
  // weekdays: 0=Montag, 1=Dienstag, ..., 6=Sonntag (wie in WEEKDAYS Array)
  const calculateRecurringDates = (startDate, weekdays, weeks) => {
    const dates = [];
    const start = new Date(startDate + 'T00:00:00');
    
    // Konvertiere UI-Wochentage (0=Mo, 6=So) zu JS-Wochentagen (0=So, 1=Mo)
    const jsWeekdays = weekdays.map(d => d === 6 ? 0 : d + 1);
    
    for (let week = 0; week < weeks; week++) {
      for (const jsWeekday of jsWeekdays.sort((a, b) => a - b)) {
        const date = new Date(start);
        // Zum Anfang der aktuellen Woche (Montag) gehen
        const startDay = start.getDay();
        const startMondayOffset = startDay === 0 ? -6 : 1 - startDay;
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() + startMondayOffset + (week * 7));
        
        // Zum gewünschten Wochentag gehen
        const targetDayOffset = jsWeekday === 0 ? 6 : jsWeekday - 1; // Mo=0, Di=1, ..., So=6
        const targetDate = new Date(weekStart);
        targetDate.setDate(weekStart.getDate() + targetDayOffset);
        
        // Nur Daten ab dem Startdatum
        if (targetDate >= start) {
          dates.push(targetDate.toISOString().split('T')[0]);
        }
      }
    }
    
    return [...new Set(dates)].sort();
  };

  // Fahrt erstellen (mit mehreren Zeitslots als EINE Fahrt)
  const handleCreateRide = async () => {
    const selectedSlots = newRide.timeSlots || [];
    if (!newRide.date || selectedSlots.length === 0 || !newRide.customerName) {
      toast.error('Bitte Datum, mindestens einen Zeitslot und Kundenname angeben');
      return;
    }
    
    setIsLoading(true);
    
    try {
      let datesToCreate = [newRide.date];
      
      // Bei wiederkehrenden Fahrten: Alle Termine berechnen
      if (newRide.isRecurring && newRide.recurringWeekdays.length > 0) {
        datesToCreate = calculateRecurringDates(
          newRide.date,
          newRide.recurringWeekdays,
          newRide.recurringWeeks
        );
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      // Für jeden Termin eine Fahrt erstellen
      for (const date of datesToCreate) {
        try {
          await axios.post(
            `${API}/bookings/admin/rides?password=${encodeURIComponent(storedPassword())}`,
            { ...newRide, date, timeSlots: selectedSlots, isRecurring: false }
          );
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Fehler für ${date}:`, err);
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} Fahrt(en) erstellt` + (errorCount > 0 ? `, ${errorCount} fehlgeschlagen` : ''));
      } else {
        toast.error('Keine Fahrten erstellt');
      }
      
      loadAdminRides();
      loadBlockedDates();
      setNewRide({
        date: '',
        timeSlot: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        memberNumber: '',
        petName: '',
        petType: 'Hund',
        startAddress: '',
        endAddress: '',
        rideType: 'tierarzt',
        price: 0,
        notes: '',
        paymentStatus: 'offen',
        deductFromFlatrate: false,
        isRecurring: false,
        recurringWeekdays: [],
        recurringWeeks: 4,
        timeSlots: []
      });
    } catch (error) {
      toast.error('Fehler beim Erstellen der Fahrt');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fahrt löschen (einzeln)
  const handleDeleteRide = async (rideId) => {
    if (!window.confirm('Fahrt wirklich löschen? Der Buchhaltungseintrag wird ebenfalls gelöscht.')) return;
    
    try {
      await axios.delete(`${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}`);
      toast.success('Fahrt gelöscht');
      setSelectedRideIds(prev => prev.filter(id => id !== rideId));
      loadAdminRides();
      loadBlockedDates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Mehrere Fahrten löschen
  const handleDeleteSelectedRides = async () => {
    if (selectedRideIds.length === 0) {
      toast.error('Keine Fahrten ausgewählt');
      return;
    }
    
    if (!window.confirm(`${selectedRideIds.length} Fahrt(en) wirklich löschen? Die zugehörigen Buchhaltungseinträge werden ebenfalls gelöscht.`)) return;
    
    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const rideId of selectedRideIds) {
      try {
        await axios.delete(`${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}`);
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Fehler beim Löschen von ${rideId}:`, error);
      }
    }
    
    setIsLoading(false);
    setSelectedRideIds([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} Fahrt(en) gelöscht` + (errorCount > 0 ? `, ${errorCount} fehlgeschlagen` : ''));
    } else {
      toast.error('Keine Fahrten konnten gelöscht werden');
    }
    
    loadAdminRides();
    loadBlockedDates();
  };

  // Fahrt-Auswahl toggle
  const toggleRideSelection = (rideId) => {
    setSelectedRideIds(prev => 
      prev.includes(rideId) 
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId]
    );
  };

  // Alle Fahrten auswählen/abwählen
  const toggleAllRides = () => {
    if (selectedRideIds.length === adminRides.length) {
      setSelectedRideIds([]);
    } else {
      setSelectedRideIds(adminRides.map(r => r.id));
    }
  };

  // Zahlungsstatus ändern
  const handleUpdateRidePayment = async (rideId, newStatus) => {
    try {
      await axios.put(
        `${API}/bookings/admin/rides/${rideId}?password=${encodeURIComponent(storedPassword())}&paymentStatus=${newStatus}`
      );
      toast.success('Zahlungsstatus aktualisiert');
      loadAdminRides();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Tier zum Formular hinzufügen
  const addPetToForm = () => {
    setFormData(prev => ({
      ...prev,
      pets: [...prev.pets, { id: Date.now().toString(), petType: '', petName: '', notes: '' }]
    }));
  };

  // Tier aus Formular entfernen
  const removePetFromForm = (petId) => {
    setFormData(prev => ({
      ...prev,
      pets: prev.pets.filter(p => p.id !== petId)
    }));
  };

  // Tier im Formular aktualisieren
  const updatePetInForm = (petId, field, value) => {
    setFormData(prev => ({
      ...prev,
      pets: prev.pets.map(p => p.id === petId ? { ...p, [field]: value } : p)
    }));
  };

  // Formular zurücksetzen
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      pets: [],
      notes: ''
    });
  };

  // Bearbeitung starten
  const startEdit = (member) => {
    setFormData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      street: member.street || '',
      houseNumber: member.houseNumber || '',
      postalCode: member.postalCode || '',
      city: member.city || '',
      pets: member.pets || [],
      notes: member.notes || ''
    });
    setView('edit');
  };

  // Gefilterte Mitglieder
  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.memberNumber.toString().includes(searchTerm) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Aktuelles Jahr Tracking finden
  const getCurrentTracking = (membership) => {
    const currentYear = new Date().getFullYear();
    return membership.tripTracking?.find(t => t.year === currentYear) || {
      tierklinikUsed: 0,
      tierklinikAvailable: 2,
      tierarztUsed: 0,
      tierarztAvailable: 3,
      bonusFromLastYear: 0
    };
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-orange-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              {loginStep === 'password' ? (
                <Lock className="h-8 w-8 text-blue-600" />
              ) : (
                <Shield className="h-8 w-8 text-blue-600" />
              )}
            </div>
            <CardTitle className="text-2xl">Admin-Bereich</CardTitle>
            <CardDescription>
              {loginStep === 'password' 
                ? 'Mitgliederverwaltung Pfotenflatrate' 
                : '2-Faktor-Authentifizierung'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginStep === 'password' ? (
              <form onSubmit={handleRequestTwoFA} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Admin-Passwort eingeben"
                    className="text-center text-lg"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading || !password}
                  data-testid="admin-login-btn"
                >
                  {isLoading ? 'Prüfe...' : 'Weiter'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <Mail className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-800">
                    Ein 6-stelliger Code wurde an<br />
                    <strong>{maskedEmail || 'Ihre E-Mail'}</strong> gesendet.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Gültig für {twoFAValidityMinutes} Minuten
                  </p>
                </div>
                
                <form onSubmit={handleVerifyTwoFA} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="twofa-code">Bestätigungscode</Label>
                    <Input
                      id="twofa-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      data-testid="2fa-code-input"
                      autoFocus
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={isLoading || twoFACode.length !== 6}
                    data-testid="verify-2fa-btn"
                  >
                    {isLoading ? (
                      <>Prüfe...</>
                    ) : (
                      <>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Code bestätigen
                      </>
                    )}
                  </Button>
                </form>
                
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleResendCode}
                    disabled={isLoading}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Neuer Code
                  </Button>
                  <Button 
                    type="button"
                    variant="ghost"
                    className="flex-1"
                    onClick={handleCancelTwoFA}
                    disabled={isLoading}
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Global E-Mail-Fehler Overlay (Banner + Dialog) - wird in JEDER View eingeblendet
  const emailOverlay = (
    <>
      {emailStatus.hasErrors && (
        <div className="bg-red-500 text-white p-3" data-testid="email-error-banner">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:justify-between md:items-center gap-2">
            <div className="flex items-start md:items-center gap-2">
              <Mail className="h-5 w-5 shrink-0 mt-0.5 md:mt-0" />
              <div className="text-sm">
                <div className="font-medium">
                  ⚠️ {emailStatus.failedCount} E-Mail(s) konnten nicht gesendet werden
                </div>
                <div className="text-red-100 text-xs mt-1">
                  {emailStatus.failedEmails[0]?.error?.includes('limit')
                    ? 'IONOS Tageslimit erreicht - wird um Mitternacht zurückgesetzt.'
                    : emailStatus.failedEmails[0]?.error?.includes('Authentication')
                      ? 'SMTP-Zugangsdaten ungültig - bitte IONOS-Passwort in den Env-Variablen aktualisieren.'
                      : 'Zugehörige Buchungen sind trotzdem gespeichert und im Admin sichtbar.'}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => setEmailErrorDialogOpen(true)}
                data-testid="email-error-details-btn"
              >
                Details anzeigen
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={clearEmailErrors}
                data-testid="email-error-clear-btn"
              >
                Ausblenden
              </Button>
            </div>
          </div>
        </div>
      )}
      <Dialog open={emailErrorDialogOpen} onOpenChange={setEmailErrorDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="email-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-red-500" />
              Fehlgeschlagene E-Mails ({emailStatus.failedCount})
            </DialogTitle>
            <DialogDescription>
              Zugehörige Buchungen sind trotzdem in der Datenbank gespeichert und unter &bdquo;Buchungen&ldquo; bzw. im Kalender sichtbar.
              Nur der automatische E-Mail-Versand hat versagt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {emailStatus.failedEmails.map((e, idx) => (
              <div
                key={idx}
                className="border border-red-200 bg-red-50 rounded-lg p-3 text-sm"
                data-testid={`email-error-item-${idx}`}
              >
                <div className="text-xs text-gray-500 mb-1">
                  {(e.timestamp || '').substring(0, 16).replace('T', ' ')}
                </div>
                <div className="font-semibold text-gray-900 break-words">
                  {e.subject}
                </div>
                <div className="text-xs text-gray-600 mt-1 break-all">
                  Empfänger: {Array.isArray(e.to) ? e.to.join(', ') : e.to}
                </div>
                <div className="text-xs text-red-700 mt-2 font-mono break-words">
                  {e.error}
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={!e.body_html}
                    onClick={() => setEmailPreviewIndex(idx)}
                    data-testid={`email-error-preview-${idx}`}
                  >
                    Inhalt anzeigen
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={!e.body_html || emailRetryingIndex === idx}
                    onClick={() => retryFailedEmail(idx)}
                    data-testid={`email-error-retry-${idx}`}
                  >
                    {emailRetryingIndex === idx ? 'Wird gesendet...' : 'Erneut senden'}
                  </Button>
                  {!e.body_html && (
                    <span className="text-xs text-gray-500 self-center">
                      (Inhalt vor Update — nicht verfügbar)
                    </span>
                  )}
                </div>
              </div>
            ))}
            {emailStatus.failedEmails.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">Keine Fehler vorhanden.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailErrorDialogOpen(false)} data-testid="email-error-dialog-close">
              Schliessen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mail-Inhalt Preview Dialog */}
      <Dialog open={emailPreviewIndex !== null} onOpenChange={(open) => !open && setEmailPreviewIndex(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Mail-Inhalt
            </DialogTitle>
            {emailPreviewIndex !== null && emailStatus.failedEmails[emailPreviewIndex] && (
              <DialogDescription>
                <span className="block font-semibold text-gray-900 mt-1">
                  {emailStatus.failedEmails[emailPreviewIndex].subject}
                </span>
                <span className="block text-xs text-gray-500 mt-1">
                  Empfänger: {(emailStatus.failedEmails[emailPreviewIndex].to || []).join(', ')}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg bg-white">
            {emailPreviewIndex !== null && (
              <iframe
                title="Mail-Inhalt"
                srcDoc={emailStatus.failedEmails[emailPreviewIndex]?.body_html || '<p style="padding:2rem;color:#6b7280;font-family:sans-serif">Kein Inhalt gespeichert.</p>'}
                className="w-full h-[60vh] border-0"
                sandbox=""
                data-testid="email-preview-iframe"
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailPreviewIndex(null)}
              data-testid="email-preview-close"
            >
              Schliessen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Settings View
  if (view === 'settings') {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-8">
        {emailOverlay}
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => setView('list')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" /> Einstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Passwort ändern</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Aktuelles Passwort</Label>
                    <Input
                      type="password"
                      value={passwordChange.current}
                      onChange={(e) => setPasswordChange(prev => ({ ...prev, current: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Neues Passwort</Label>
                    <Input
                      type="password"
                      value={passwordChange.new}
                      onChange={(e) => setPasswordChange(prev => ({ ...prev, new: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Neues Passwort bestätigen</Label>
                    <Input
                      type="password"
                      value={passwordChange.confirm}
                      onChange={(e) => setPasswordChange(prev => ({ ...prev, confirm: e.target.value }))}
                    />
                  </div>
                  <Button onClick={handleChangePassword} className="w-full">
                    Passwort ändern
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }


  // Calendar Blocking View
  if (view === 'calendar') {
    return (
      <>
        {emailOverlay}
        <AdminCalendar 
          storedPassword={storedPassword}
          onBack={() => setView('list')}
        />
      </>
    );
  }
  // Accounting View
  if (view === 'accounting') {
    return (
      <>
        {emailOverlay}
        <AccountingPanel 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Customer Registry View
  if (view === 'customers') {
    return (
      <>
        {emailOverlay}
        <CustomerRegistry 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Dashboard View
  if (view === 'dashboard') {
    return (
      <>
        {emailOverlay}
        <DashboardPanel 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Analytics View
  if (view === 'analytics') {
    return (
      <>
        {emailOverlay}
        <AnalyticsPanel 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Manuelle Rechnungen
  if (view === 'manual-invoices') {
    return (
      <>
        {emailOverlay}
        <AdminManualInvoices
          password={storedPassword()}
          onBack={() => setView('list')}
        />
      </>
    );
  }

  // Kostenvoranschläge
  if (view === 'manual-quotes') {
    return (
      <>
        {emailOverlay}
        <AdminManualQuotes
          password={storedPassword()}
          onBack={() => setView('list')}
        />
      </>
    );
  }

  // Backup View
  if (view === 'backup') {
    return (
      <>
        {emailOverlay}
        <BackupPanel 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Combo Transport View
  if (view === 'combo-transport') {
    return (
      <>
        {emailOverlay}
        <ComboTransportAdmin 
          password={storedPassword()} 
          onBack={() => setView('list')} 
        />
      </>
    );
  }

  // Rundmail View
  if (view === 'newsletter') {
    return (
      <>
        {emailOverlay}
        <AdminNewsletter
          password={storedPassword()}
          members={members}
          onBack={() => setView('list')}
        />
      </>
    );
  }

  // Banner Management View
  if (view === 'banner') {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-8">
        {emailOverlay}
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => setView('list')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
          <InfoBannerAdmin />
        </div>
      </div>
    );
  }

  // Detail View
  if (view === 'detail' && selectedMember) {
    const getPetById = (petId) => selectedMember.pets?.find(p => p.id === petId);
    
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-8">
        {emailOverlay}
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => { setView('list'); setSelectedMember(null); }} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" /> Zurück zur Liste
          </Button>
          
          <Card className="mb-6">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <CardDescription className="text-blue-100">Mitglied #{String(selectedMember.memberNumber).padStart(6, '0')}</CardDescription>
                  <CardTitle className="text-2xl">{selectedMember.name}</CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => startEdit(selectedMember)}>
                    <Edit className="h-4 w-4 mr-1" /> Bearbeiten
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDeleteMember(selectedMember.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Kontaktdaten */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Kontaktdaten</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">E-Mail:</span> {selectedMember.email || '-'}</p>
                    <p><span className="text-gray-500">Telefon:</span> {selectedMember.phone || '-'}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Adresse</h3>
                  <div className="space-y-1 text-sm">
                    <p>{selectedMember.street} {selectedMember.houseNumber}</p>
                    <p>{selectedMember.postalCode} {selectedMember.city}</p>
                  </div>
                </div>
              </div>

              {/* Notizen */}
              {selectedMember.notes && (
                <div className="mb-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-800 mb-2">📝 Notizen</h3>
                  <p className="text-sm text-yellow-900 whitespace-pre-wrap">{selectedMember.notes}</p>
                </div>
              )}

              {/* Tiere */}
              <div className="mb-8">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <PawPrint className="h-5 w-5 mr-2 text-orange-500" />
                  Tiere ({selectedMember.pets?.length || 0})
                </h3>
                {selectedMember.pets?.length > 0 ? (
                  <div className="grid gap-3">
                    {selectedMember.pets.map(pet => {
                      const hasMembership = selectedMember.memberships?.some(m => m.petId === pet.id && m.active);
                      return (
                        <div key={pet.id} className="p-4 bg-gray-50 rounded-lg border">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{pet.petName}</p>
                              <p className="text-sm text-gray-600">{pet.petType}</p>
                              {pet.notes && (
                                <p className="text-xs text-gray-500 mt-1 italic">&ldquo;{pet.notes}&rdquo;</p>
                              )}
                            </div>
                            {!hasMembership && (
                              <Button size="sm" variant="outline" onClick={() => handleAddMembership(pet.id)}>
                                <Plus className="h-4 w-4 mr-1" /> Mitgliedschaft
                              </Button>
                            )}
                            {hasMembership && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                ✓ Mitglied
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine Tiere eingetragen</p>
                )}
              </div>

              {/* Mitgliedschaften & Fahrten */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <Car className="h-5 w-5 mr-2 text-blue-500" />
                  Mitgliedschaften & Fahrten
                </h3>
                {selectedMember.memberships?.length > 0 ? (
                  <div className="space-y-4">
                    {selectedMember.memberships.filter(m => m.active).map(membership => {
                      const pet = getPetById(membership.petId);
                      const tracking = getCurrentTracking(membership);
                      
                      return (
                        <div key={membership.id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="font-medium text-blue-900">
                                {pet?.petName || 'Unbekanntes Tier'} ({pet?.petType})
                              </p>
                              <p className="text-xs text-blue-600">
                                Seit: {new Date(membership.startDate).toLocaleDateString('de-DE')}
                              </p>
                              {tracking.bonusFromLastYear > 0 && (
                                <p className="text-xs text-green-600 mt-1">
                                  🎁 +{tracking.bonusFromLastYear} Bonus-Fahrt aus Vorjahr
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Tierklinik Fahrten */}
                          <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium flex items-center">
                                <Building2 className="h-4 w-4 mr-1 text-red-500" />
                                Tierklinik
                              </span>
                              <span className="text-sm text-gray-600">
                                {tracking.tierklinikUsed} / {tracking.tierklinikUsed + tracking.tierklinikAvailable} genutzt
                              </span>
                            </div>
                            <div className="flex gap-2">
                              {[...Array(tracking.tierklinikUsed + tracking.tierklinikAvailable)].map((_, i) => (
                                <button
                                  key={i}
                                  onClick={() => handleTripAction(
                                    membership.id,
                                    'tierklinik',
                                    i < tracking.tierklinikUsed ? 'restore' : 'use'
                                  )}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                                    i < tracking.tierklinikUsed
                                      ? 'bg-red-500 text-white hover:bg-red-600'
                                      : 'bg-gray-200 text-gray-400 hover:bg-green-500 hover:text-white'
                                  }`}
                                  title={i < tracking.tierklinikUsed ? 'Klick zum Wiederherstellen' : 'Klick zum Markieren als genutzt'}
                                >
                                  {i < tracking.tierklinikUsed ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Tierarzt Fahrten */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium flex items-center">
                                <PawPrint className="h-4 w-4 mr-1 text-blue-500" />
                                Tierarzt
                              </span>
                              <span className="text-sm text-gray-600">
                                {tracking.tierarztUsed} / {tracking.tierarztUsed + tracking.tierarztAvailable} genutzt
                              </span>
                            </div>
                            <div className="flex gap-2">
                              {[...Array(tracking.tierarztUsed + tracking.tierarztAvailable)].map((_, i) => (
                                <button
                                  key={i}
                                  onClick={() => handleTripAction(
                                    membership.id,
                                    'tierarzt',
                                    i < tracking.tierarztUsed ? 'restore' : 'use'
                                  )}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                                    i < tracking.tierarztUsed
                                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                                      : 'bg-gray-200 text-gray-400 hover:bg-green-500 hover:text-white'
                                  }`}
                                  title={i < tracking.tierarztUsed ? 'Klick zum Wiederherstellen' : 'Klick zum Markieren als genutzt'}
                                >
                                  {i < tracking.tierarztUsed ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine aktiven Mitgliedschaften</p>
                )}
              </div>

              {/* Dokumente */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-purple-500" />
                  Vertragsdokumente
                </h3>
                
                {/* Upload-Bereich */}
                <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-200">
                  <div className="flex flex-wrap gap-3 items-center">
                    <select
                      value={uploadDocType}
                      onChange={(e) => setUploadDocType(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm bg-white"
                    >
                      <option value="vertrag">Vertrag</option>
                      <option value="datenschutz">Datenschutzerklärung</option>
                      <option value="widerruf">Widerrufsbelehrung</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleDocumentUpload}
                      className="hidden"
                      id="doc-upload"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingDoc}
                      className="bg-white"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isUploadingDoc ? 'Lädt...' : 'Dokument hochladen'}
                    </Button>
                    <span className="text-xs text-gray-500">PDF, JPG, PNG, DOC (max. 10 MB)</span>
                  </div>
                </div>

                {/* Dokumenten-Liste */}
                {memberDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {memberDocuments.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <File className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium">{doc.documentTypeLabel}</p>
                            <p className="text-xs text-gray-500">
                              {doc.originalFilename} • {(doc.fileSize / 1024).toFixed(1)} KB • 
                              {new Date(doc.uploadedAt).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadDocument(doc)}
                            title="Herunterladen"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine Dokumente vorhanden</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Edit/New View
  if (view === 'edit' || view === 'new') {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-8">
        {emailOverlay}
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => { setView(selectedMember ? 'detail' : 'list'); resetForm(); }} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" /> Abbrechen
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle>{view === 'new' ? 'Neues Mitglied' : 'Mitglied bearbeiten'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Persönliche Daten */}
              <div className="space-y-4">
                <h3 className="font-semibold">Persönliche Daten</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Vor- und Nachname"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>E-Mail</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="email@beispiel.de"
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="0123 456789"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-4">
                <h3 className="font-semibold">Adresse</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label>Straße</Label>
                      <Input
                        value={formData.street}
                        onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Hausnr.</Label>
                      <Input
                        value={formData.houseNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, houseNumber: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>PLZ</Label>
                      <Input
                        value={formData.postalCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Stadt</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tiere */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold">Tiere</h3>
                  <Button size="sm" variant="outline" onClick={addPetToForm}>
                    <Plus className="h-4 w-4 mr-1" /> Tier hinzufügen
                  </Button>
                </div>
                {formData.pets.map((pet, index) => (
                  <div key={pet.id} className="p-4 bg-gray-50 rounded-lg border space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">Tier {index + 1}</span>
                      <Button size="sm" variant="ghost" onClick={() => removePetFromForm(pet.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tierart</Label>
                        <Input
                          value={pet.petType}
                          onChange={(e) => updatePetInForm(pet.id, 'petType', e.target.value)}
                          placeholder="z.B. Hund, Katze"
                        />
                      </div>
                      <div>
                        <Label>Name des Tieres</Label>
                        <Input
                          value={pet.petName}
                          onChange={(e) => updatePetInForm(pet.id, 'petName', e.target.value)}
                          placeholder="Name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Besonderheiten</Label>
                      <Input
                        value={pet.notes}
                        onChange={(e) => updatePetInForm(pet.id, 'notes', e.target.value)}
                        placeholder="z.B. ängstlich, braucht Transportbox"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Notizen */}
              <div className="space-y-2">
                <Label>Allgemeine Notizen</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full h-24 p-3 border rounded-lg resize-none"
                  placeholder="Besonderheiten, wichtige Hinweise..."
                />
              </div>

              {/* Speichern */}
              <Button 
                onClick={view === 'new' ? handleCreateMember : handleUpdateMember}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? 'Speichere...' : 'Speichern'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List View (Default)
  return (
    <div className="min-h-screen bg-gray-100">
      {emailOverlay}
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Pfotenflatrate Admin</h1>
              <p className="text-blue-100">Mitgliederverwaltung</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="secondary" size="sm" onClick={() => setView('dashboard')} data-testid="dashboard-btn">
                <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('analytics')} data-testid="analytics-btn">
                <BarChart3 className="h-4 w-4 mr-2" /> Besucher
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('customers')} data-testid="customer-registry-btn">
                <BookUser className="h-4 w-4 mr-2" /> Kundenregister
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('accounting')}>
                <DollarSign className="h-4 w-4 mr-2" /> Buchhaltung
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('manual-invoices')} data-testid="manual-invoices-btn">
                <FileText className="h-4 w-4 mr-2" /> Rechnungen
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('manual-quotes')} data-testid="manual-quotes-btn">
                <FileText className="h-4 w-4 mr-2" /> Kostenvoranschläge
              </Button>
              <Button variant="secondary" size="sm" onClick={() => { setView('calendar'); loadBlockedDates(); loadAdminRides(); }}>
                <Calendar className="h-4 w-4 mr-2" /> Kalender
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('backup')} data-testid="backup-btn">
                <Database className="h-4 w-4 mr-2" /> Backup
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('combo-transport')} data-testid="combo-transport-btn">
                <Truck className="h-4 w-4 mr-2" /> Kombi-Transport
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('banner')} data-testid="banner-btn">
                <Megaphone className="h-4 w-4 mr-2" /> Banner
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('newsletter')} data-testid="newsletter-btn">
                <Mail className="h-4 w-4 mr-2" /> Rundmail
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setView('settings')}>
                <Settings className="h-4 w-4 mr-2" /> Einstellungen
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{stats.totalMembers}</div>
                <div className="text-sm text-blue-100">Mitglieder</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{stats.activeMemberships}</div>
                <div className="text-sm text-blue-100">Aktive Mitgliedschaften</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{stats.totalPets}</div>
                <div className="text-sm text-blue-100">Tiere</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suche nach Name, Nr. oder E-Mail..."
              className="pl-10"
            />
          </div>
          <Button onClick={() => { resetForm(); setView('new'); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Neues Mitglied
          </Button>
        </div>

        {/* Ausstehende Registrierungen - IMMER SICHTBAR */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Ausstehende Registrierungen ({pendingRegistrations.length})
          </h3>
          {pendingRegistrations.length > 0 ? (
            <div className="grid gap-3">
              {pendingRegistrations.map(reg => (
                <Card key={reg.registrationId} className="border-l-4 border-orange-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold">{reg.name}</h4>
                        <p className="text-sm text-gray-500">
                          {reg.email} · {reg.phone}
                        </p>
                        <p className="text-sm text-gray-500">
                          {reg.pets?.length || 0} Tier(e) · {reg.tariffType === 'nachbarort' ? 'Nachbarort-Tarif' : 'Standard-Tarif'} · {reg.totalPrice?.toFixed(2)} €
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          Status: {reg.status === 'pending' ? 'Warten auf Zahlung' :
                                   reg.status === 'pending_confirmation' ? 'Warten auf E-Mail-Bestätigung' : 
                                   reg.status === 'confirmed' ? 'Bestätigt - Warten auf Zahlung' : 
                                   reg.status === 'awaiting_payment' ? 'Warten auf Zahlung' :
                                   reg.status}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <span className="text-xs text-gray-400">
                          {reg.createdAt ? new Date(reg.createdAt).toLocaleDateString('de-DE') : ''}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={(e) => { e.stopPropagation(); confirmPayment(reg.registrationId || reg.id); }}
                          >
                            ✓ Zahlung bestätigen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                            onClick={(e) => { e.stopPropagation(); deletePendingRegistration(reg.registrationId || reg.id); }}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-6 text-center border-dashed border-2 border-gray-200">
              <Mail className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">Keine ausstehenden Registrierungen</p>
            </Card>
          )}
        </div>

        {/* Member List */}
        <div className="grid gap-4">
          {filteredMembers.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {searchTerm ? 'Keine Mitglieder gefunden' : 'Noch keine Mitglieder vorhanden'}
              </p>
            </Card>
          ) : (
            filteredMembers.map(member => (
              <Card 
                key={member.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setSelectedMember(member); setView('detail'); }}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold">#{String(member.memberNumber).padStart(6, '0')}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold">{member.name}</h3>
                        <p className="text-sm text-gray-500">
                          {member.pets?.length || 0} Tier(e) · {member.memberships?.filter(m => m.active).length || 0} Mitgliedschaft(en)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" title="Details anzeigen">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Mitglied löschen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMember(member.id);
                        }}
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
